import { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';

import { styled } from '@mui/material/styles';
import { FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import PropTypes from 'prop-types';
import _ from 'lodash';

import SmallButton from '../../components/layout/atoms/SmallButton';
import IconHeading from '../../components/layout/molecules/IconHeading';
import Loading from '../../components/layout/organisms/Loading/Loading';

import { UPDATE_PATIENT_MED_HISTORY } from '../../actions/types';
import { getMostRecentHealthData, updatePatientMedicalHistory } from '../../actions/healthData';
import { dateFormated } from '../../utils/helpers';
import { datePickerLangText } from '../../i18n';
import { EditIcon, FileTextIcon, HeartIcon, XIcon } from '../../resources/icons';
import {
  ABSENT,
  DIABETES_TYPE_UNKNOWN,
  GESTATIONAL_DIABETES,
  MILD,
  MODERATE,
  NO_DIABETES,
  NO_DISEASE,
  PDR,
  PRESENT,
  SEVERE,
  TYPE_1_DIABETES,
  TYPE_2_DIABETES,
} from '../../constants/medicalRecords';

const PREFIX = 'MedicalHistory';

const classes = {
  datePicker: `${PREFIX}-datePicker`,
};

const Root = styled('form', {
  shouldForwardProp(propName) {
    return !['disallowEdit', 'customHeight'].includes(propName);
  },
})(({ theme, disallowEdit, customHeight }) => ({
  height: customHeight ? customHeight : 'auto',

  [`& .${classes.datePicker}`]: {
    margin: '0',
  },
}));

const MedicalHistory = (props) => {
  const {
    medHistory,
    patientDetails,
    created,
    disabled,
    disallowEdit,
    updatePatientMedicalHistory,
    getMostRecentHealthData,
  } = props;

  const initialState = {
    diabetes_type: { values: 0 },
    diabetes_onset: { values: null },
    eye_surgery_history: { values: 0 },
    heart_disease: { values: 0 },
    high_blood_pressure: { values: 0 },
    high_cholesterol: { values: 0 },
    dr_presence: { values: 0 },
    me_presence: { values: 0 },
  };

  // ensure that empty data is not sent to server
  const filterInputsEmptyValues = (rawInputs) => {
    let filteredInputs = {};
    if (typeof rawInputs === 'object' && !_.isEmpty(rawInputs)) {
      Object.entries(rawInputs).map(([fieldName, field]) => {
        if (fieldName === 'diabetes_onset') {
          if (![undefined, medHistory?.[fieldName]?.values].includes(field?.values)) {
            filteredInputs[fieldName] = field.values;
          }
        } else if (![undefined, null, medHistory?.[fieldName]?.values].includes(field?.values)) {
          filteredInputs[fieldName] = field.values;
        }
      });
    }

    return filteredInputs;
  };

  const filterInputsNullToEmptyString = (rawInputs, isEditing) => {
    let filteredInputs = _.cloneDeep(initialState);
    if (typeof rawInputs === 'object' && rawInputs !== null) {
      for (const field in rawInputs) {
        if (field in initialState) {
          let value =
            rawInputs[field] && typeof rawInputs[field] === 'object' && 'values' in rawInputs[field]
              ? rawInputs[field].values
              : rawInputs[field];

          if (field === 'diabetes_onset') {
            filteredInputs[field].values = value ? value : null;
          } else {
            if (value === null || value === undefined) value = 0;
            if (isEditing && value === 0) value = '';
            filteredInputs[field].values = value;
          }
        }
      }
    }

    return filteredInputs;
  };

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const staticWords = useSelector((state) => state.handlingTranslation.words);
  const { promiseInProgress } = usePromiseTracker({ area: 'medical-history-area' });
  const [values, setValues] = useState(medHistory ? filterInputsNullToEmptyString(medHistory) : initialState);
  const [showButtonLoading, setShowButtonLoading] = useState(false);
  const [datefieldError, setDateFieldError] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const updatingNewValuesWithEmptyValues = (newValues, value) => {
    const result = _.cloneDeep(newValues);
    Object.keys(result).forEach((key) => {
      if (key !== 'diabetes_onset') {
        result[key].values = ['', 0].includes(result[key].values) ? value : result[key].values;
      }
    });
    return result;
  };

  const handleChange = (event) => {
    setValues((prevState) => ({
      ...prevState,
      [event.target.name]: { values: event.target.value },
    }));
  };

  const handleReset = (event) => {
    if (event) event.preventDefault();
    setEditMode(false);
    const newValues = filterInputsNullToEmptyString(medHistory || {}, false);
    setValues(newValues);
  };

  const handleDateChange = (date) => {
    setValues({ ...values, diabetes_onset: { values: date } });
  };

  const formatDate = (date) => {
    if (typeof date?.getMonth !== 'function') return null;
    return date;
  };

  const handleSubmit = async (event) => {
    if (datefieldError) {
      return;
    }

    event.preventDefault();
    setShowButtonLoading(true);
    try {
      let medicalHistory = { ...values };
      medicalHistory = updatingNewValuesWithEmptyValues(medicalHistory, 0);
      medicalHistory = filterInputsEmptyValues(medicalHistory);
      let previousValues;

      if (!_.isEmpty(medHistory)) {
        previousValues = filterInputsNullToEmptyString(medHistory);
      }
      const getChangedValues = (previous, current) => {
        const changedValues = {};
        for (const key in current) {
          // Check if the key exists in previousValues and compare the values
          changedValues[key] = current[key];
        }

        return changedValues;
      };

      // Generate the result with changed values
      const filteredMedicalHistory = getChangedValues(previousValues, medicalHistory);
      const keysToCheck = ['eye_surgery_history', 'heart_disease', 'high_blood_pressure', 'high_cholesterol'];
      keysToCheck.forEach((key) => {
        if (filteredMedicalHistory[key] === 0) {
          filteredMedicalHistory[key] = null;
        }
      });
      if ('diabetes_onset' in filteredMedicalHistory) {
        const dateString = formatDate(filteredMedicalHistory.diabetes_onset);
        filteredMedicalHistory['diabetes_onset'] = dateFormated(dateString);
      }
      dispatch({
        type: UPDATE_PATIENT_MED_HISTORY,
        payload: '',
      });

      await updatePatientMedicalHistory(patientDetails.id, filteredMedicalHistory);
    } catch (e) {
      setShowButtonLoading(false);
    } finally {
      setShowButtonLoading(false);
      setEditMode(false);
    }
  };

  const handleEdit = () => {
    setEditMode(true);
    let newValues = { ...values };
    newValues = updatingNewValuesWithEmptyValues(newValues, '');
    newValues = filterInputsNullToEmptyString(newValues, true);
    setValues(newValues);
  };

  useEffect(() => {
    const newValues = filterInputsNullToEmptyString(medHistory);
    setValues(newValues);
  }, [medHistory]);

  useEffect(() => {
    if (created !== '') {
      getMostRecentHealthData(patientDetails.id, 'medical-history-area');
    }
  }, [created]);

  return (
    <Root autoComplete="off" customHeight={props?.customHeight} disallowEdit={disallowEdit} onSubmit={handleSubmit}>
      <Grid container alignItems="center" justifyContent="space-between" mb={3}>
        <Grid item>
          <IconHeading title="Medical History" icon={<HeartIcon />} />
        </Grid>
        <Grid>
          {disallowEdit || promiseInProgress ? null : editMode ? (
            <Grid container item display="flex" gap={1}>
              <Grid item>
                <SmallButton startIcon={<FileTextIcon />} onClick={handleSubmit} showLoading={showButtonLoading}>
                  {staticWords.Save}
                </SmallButton>
              </Grid>
              <Grid item>
                <SmallButton type="button" startIcon={<XIcon />} onClick={handleReset} showLoading={showButtonLoading}>
                  {staticWords.Cancel}
                </SmallButton>
              </Grid>
            </Grid>
          ) : (
            <Grid item>
              <SmallButton startIcon={<EditIcon />} onClick={handleEdit} showLoading={showButtonLoading}>
                {staticWords.Edit_Data}
              </SmallButton>
            </Grid>
          )}
        </Grid>
      </Grid>
      {promiseInProgress ? (
        <Grid mt={12}>
          <Loading area="medical-history-area" />
        </Grid>
      ) : (
        <>
          <Grid container spacing={disallowEdit ? 2 : 4} justifyContent="center">
            <Grid item xs={6}>
              <FormControl
                className={classes.formControl}
                fullWidth
                disabled={disabled || !editMode}
                data-testid="select-diabetes-type"
              >
                <InputLabel id="select-diabetes-type-label">{staticWords.Diabetes_Type}</InputLabel>
                <Select
                  labelId="select-diabetes-type-label"
                  id="select-diabetes-type"
                  name="diabetes_type"
                  value={values.diabetes_type?.values}
                  disableUnderline={disallowEdit}
                  IconComponent={() => (disallowEdit ? null : <ArrowDropDown />)}
                  renderValue={(value) => {
                    switch (value) {
                      case NO_DIABETES:
                        return staticWords.No_diabetes;
                      case TYPE_1_DIABETES:
                        return staticWords.Type_1_Diabetes;
                      case TYPE_2_DIABETES:
                        return staticWords.Type_2_Diabetes;
                      case GESTATIONAL_DIABETES:
                        return staticWords.Gestational_Diabetes;
                      default:
                        return '-';
                    }
                  }}
                  onChange={handleChange}
                >
                  <MenuItem value={0}>
                    <em>-</em>
                  </MenuItem>
                  {[
                    { optionKey: NO_DIABETES, optionValue: staticWords.No_diabetes },
                    {
                      optionKey: TYPE_1_DIABETES,
                      optionValue: staticWords.Type_1_Diabetes,
                    },
                    {
                      optionKey: TYPE_2_DIABETES,
                      optionValue: staticWords.Type_2_Diabetes,
                    },
                    {
                      optionKey: GESTATIONAL_DIABETES,
                      optionValue: staticWords.Gestational_Diabetes,
                    },
                  ].map((item) => {
                    return (
                      <MenuItem key={item.optionKey} value={item.optionKey}>
                        {item.optionValue}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={datePickerLangText(i18n.language)}>
                <DesktopDatePicker
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          value: disallowEdit || !editMode ? params.inputProps.value || '-' : params.inputProps.value,
                          placeholder: '-',
                        }}
                        sx={{ width: '100%' }}
                      />
                    );
                  }}
                  OpenPickerButtonProps={{
                    style: { display: disallowEdit && 'none' },
                  }}
                  fullWidth
                  margin="none"
                  variant="inline"
                  className={classes.datePicker}
                  disableFuture
                  inputFormat="dd/MM/yyyy"
                  label={staticWords.Diabetes_Onset}
                  value={values.diabetes_onset.values}
                  KeyboardButtonProps={{
                    'aria-label': 'change diabetes onset',
                  }}
                  helperText={staticWords.When_were_you_first_diagnosed_with_diabetes_}
                  disabled={disabled || !editMode}
                  autoOk
                  InputProps={{ disableUnderline: disallowEdit && true }}
                  onError={(error) => {
                    error === null ? setDateFieldError(false) : setDateFieldError(true);
                  }}
                  onChange={handleDateChange}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6}>
              <FormControl className={classes.formControl} fullWidth disabled={disabled || !editMode}>
                <InputLabel id="select-eye-surgery-history-label">{staticWords.Eye_Surgery_History}</InputLabel>
                <Select
                  labelId="select-eye-surgery-history-label"
                  name="eye_surgery_history"
                  id="eye-surgery-history"
                  value={values.eye_surgery_history.values}
                  disableUnderline={disallowEdit}
                  IconComponent={() => (disallowEdit ? null : <ArrowDropDown />)}
                  renderValue={(value) => {
                    if (value === null || value === undefined || value === 0) return '-';
                    return value ? staticWords.Had_eye_surgery : staticWords.Never_had_eye_surgery;
                  }}
                  onChange={handleChange}
                >
                  <MenuItem value={0}>
                    <em>-</em>
                  </MenuItem>
                  <MenuItem value={false}>{staticWords.Never_had_eye_surgery}</MenuItem>
                  <MenuItem value={true}>{staticWords.Had_eye_surgery}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl className={classes.formControl} fullWidth disabled={disabled || !editMode}>
                <InputLabel id="select-heart-disease-label">{staticWords.Heart_Disease}</InputLabel>
                <Select
                  labelId="select-heart-disease-label"
                  name="heart_disease"
                  id="heart-disease-history"
                  value={values?.heart_disease?.values}
                  disableUnderline={disallowEdit}
                  IconComponent={() => (disallowEdit ? null : <ArrowDropDown />)}
                  renderValue={(value) => {
                    if (value === null || value === undefined || value === 0) return '-';
                    return value ? staticWords.Has_heart_disease : staticWords.No_heart_disease;
                  }}
                  onChange={handleChange}
                >
                  <MenuItem value={0}>
                    <em>-</em>
                  </MenuItem>
                  <MenuItem value={false}>{staticWords.No_heart_disease}</MenuItem>
                  <MenuItem value={true}>{staticWords.Has_heart_disease}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl className={classes.formControl} fullWidth disabled={disabled || !editMode}>
                <InputLabel id="select-high-blood-pressure-label">{staticWords.High_Blood_Pressure}</InputLabel>
                <Select
                  labelId="select-high-blood-pressure-label"
                  name="high_blood_pressure"
                  id="high-blood-pressure-history"
                  value={values.high_blood_pressure.values}
                  disableUnderline={disallowEdit}
                  IconComponent={() => (disallowEdit ? null : <ArrowDropDown />)}
                  renderValue={(value) => {
                    if (value === null || value === undefined || value === 0) return '-';
                    return value ? staticWords.Yes : staticWords.No;
                  }}
                  onChange={handleChange}
                >
                  <MenuItem value={0}>
                    <em>-</em>
                  </MenuItem>
                  <MenuItem value={true}>{staticWords.Yes}</MenuItem>
                  <MenuItem value={false}>{staticWords.No}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl className={classes.formControl} fullWidth disabled={disabled || !editMode}>
                <InputLabel id="select-high-cholesterol-label">{staticWords.High_Cholesterol}</InputLabel>
                <Select
                  labelId="select-high-cholesterol-label"
                  name="high_cholesterol"
                  id="high-cholesterol-history"
                  value={values.high_cholesterol.values}
                  disableUnderline={disallowEdit}
                  IconComponent={() => (disallowEdit ? null : <ArrowDropDown />)}
                  renderValue={(value) => {
                    if (
                      values.high_cholesterol.values === null ||
                      values.high_cholesterol.values === undefined ||
                      value === 0
                    )
                      return '-';
                    return value ? staticWords.Yes : staticWords.No;
                  }}
                  onChange={handleChange}
                >
                  <MenuItem value={0}>
                    <em>-</em>
                  </MenuItem>
                  <MenuItem value={true}>{staticWords.Yes}</MenuItem>
                  <MenuItem value={false}>{staticWords.No}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl className={classes.formControl} fullWidth disabled={disabled || !editMode}>
                <InputLabel id="select-dr-presence-label">{staticWords.DR_Presence}</InputLabel>
                <Select
                  labelId="select-dr-presence-label"
                  name="dr_presence"
                  id="dr-presence-history"
                  value={values.dr_presence.values}
                  disableUnderline={disallowEdit}
                  IconComponent={() => (disallowEdit ? null : <ArrowDropDown />)}
                  renderValue={(value) => {
                    switch (value) {
                      case NO_DISEASE:
                        return staticWords.No_Disease;
                      case MILD:
                        return staticWords.Mild_Retinopathy;
                      case MODERATE:
                        return staticWords.Moderate_Retinopathy;
                      case SEVERE:
                        return staticWords.Severe_Retinopathy;
                      case PDR:
                        return staticWords.Proliferative_Retinopathy;
                      default:
                        return '-';
                    }
                  }}
                  onChange={handleChange}
                >
                  <MenuItem value={0}>
                    <em>-</em>
                  </MenuItem>
                  <MenuItem value={NO_DISEASE}>{staticWords.No_Disease}</MenuItem>
                  <MenuItem value={MILD}>{staticWords.Mild_Retinopathy}</MenuItem>
                  <MenuItem value={MODERATE}>{staticWords.Moderate_Retinopathy}</MenuItem>
                  <MenuItem value={SEVERE}>{staticWords.Severe_Retinopathy}</MenuItem>
                  <MenuItem value={PDR}>{staticWords.Proliferative_Retinopathy}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl className={classes.formControl} fullWidth disabled={disabled || !editMode}>
                <InputLabel id="select-macular-edema-presence-label">{staticWords.Macular_Edema_Presence}</InputLabel>
                <Select
                  defaultValue=""
                  labelId="select-macular-edema-presence-label"
                  name="me_presence"
                  id="macular-edema-presence-history"
                  value={values.me_presence.values}
                  disableUnderline={disallowEdit}
                  IconComponent={() => (disallowEdit ? null : <ArrowDropDown />)}
                  renderValue={(value) => {
                    if (value === null || value === undefined || value === 0) return '-';
                    else if (value === ABSENT) return staticWords.Absent;
                    else if (value === PRESENT) return staticWords.Present;
                  }}
                  onChange={handleChange}
                >
                  <MenuItem value={0}>
                    <em>-</em>
                  </MenuItem>
                  <MenuItem value={ABSENT}>{staticWords.Absent}</MenuItem>
                  <MenuItem value={PRESENT}>{staticWords.Present}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </>
      )}
    </Root>
  );
};

MedicalHistory.propTypes = {
  medHistory: PropTypes.object,
  patientDetails: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  patientDetails: state.patients.patientDetails,
  medHistory: state.healthData.medHistory,
  created: state.healthData.created,
});

export default connect(mapStateToProps, { updatePatientMedicalHistory, getMostRecentHealthData })(MedicalHistory);
